import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../store/auth';

type Mode = 'super-admin' | 'owner' | 'agent' | 'customer';
const TABS: { mode: Mode; label: string }[] = [
  { mode: 'super-admin', label: 'Super Admin' },
  { mode: 'owner', label: 'Owner' },
  { mode: 'agent', label: 'Agent' },
  { mode: 'customer', label: 'Customer' },
];

// Seed-credential presets are DEV-ONLY. `import.meta.env.DEV` is true only under
// `vite dev`, so built bundles ship blank fields and switching tabs only changes
// which endpoint is called — it never fills in a password.
const DEV = import.meta.env.DEV;

const DEV_PRESETS: Record<Mode, { email: string; password: string }> = {
  'super-admin': { email: 'admin@runtechbyte.com', password: 'admin@123' },
  owner: { email: 'sanjay@runtechbyte.com', password: 'owner@123' },
  agent: { email: 'aisha@runtechbyte.com', password: 'agent@123' },
  customer: { email: 'anita@spicegarden.in', password: 'customer@123' },
};

export default function LoginPage() {
  const [mode, setMode] = useState<Mode>('owner');
  const [tenantSlug, setSlug] = useState(DEV ? 'runtechbyte' : '');
  const [email, setEmail] = useState(DEV ? DEV_PRESETS.owner.email : '');
  const [password, setPassword] = useState(DEV ? DEV_PRESETS.owner.password : '');
  const [busy, setBusy] = useState(false);
  const setSession = useAuth((s) => s.setSession);
  const nav = useNavigate();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const path = `/auth/${mode}/login`;
      const body =
        mode === 'super-admin'
          ? { email, password }
          : { tenantSlug, email, password };
      const res = await api.post(path, body);
      setSession(res.data);
      toast.success(`Welcome, ${res.data.actor.name}`);
      nav(mode === 'customer' ? '/portal' : '/');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error ?? 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  function setPreset(m: Mode) {
    setMode(m);
    // In a build this clears the form rather than filling it, so a previous
    // role's typed credentials never carry across a tab switch.
    setEmail(DEV ? DEV_PRESETS[m].email : '');
    setPassword(DEV ? DEV_PRESETS[m].password : '');
    if (m !== 'super-admin') setSlug(DEV ? 'runtechbyte' : '');
  }

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-brand-50 via-white to-brand-100 p-6">
      <div className="w-full max-w-md card p-7">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-brand-500 text-white grid place-items-center font-bold text-xl">T</div>
          <div>
            <h1 className="text-xl font-semibold">SupportByte</h1>
            <p className="text-xs text-slate-500">RunTechByte helpdesk suite</p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-1 p-1 bg-slate-100 rounded-lg mb-5 text-xs">
          {TABS.map((t) => (
            <button
              key={t.mode}
              onClick={() => setPreset(t.mode)}
              className={
                'py-2 rounded-md font-medium transition-colors ' +
                (mode === t.mode ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-600 hover:text-slate-900')
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode !== 'super-admin' && (
            <div>
              <label className="label">Tenant slug</label>
              <input className="input" value={tenantSlug} onChange={(e) => setSlug(e.target.value)} required />
            </div>
          )}
          <div>
            <label className="label">Email</label>
            <input type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" name="ttb-email" />
          </div>
          <div>
            <label className="label">Password</label>
            <input type="password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" name="ttb-password" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full">
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <div className="mt-5 text-[11px] text-slate-500 leading-relaxed">
          {DEV && 'Tabs preset to seed credentials. '}
          Super Admin manages all tenants. Owner/Agent/Customer log into a single tenant.
        </div>
      </div>
    </div>
  );
}
